import { useMemo } from 'react';

import { subHours } from 'date-fns';
import { isEqual } from 'es-toolkit';
import * as z from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type Praetor, getListPraetorsQueryOptions } from '@praetoid/client';

import { queryClient } from './queryClient';

z.config(z.locales.en());

export const periods = ['1h', '24h', '7d', 'all'] as const;

export const AppFilterOps = [
	'==',
	'!=',
	'=~',
	'empty',
	'not_empty',
	'<=',
	'<',
	'>=',
	'>',
	'in',
	'not_in',
	'contains',
	'not_contains',
	'like',
	'not_like',
	'like_and',
	'like_or',
	'ilike',
	'not_ilike',
	'ilike_and',
	'ilike_or'
] as const;

export const periodFilters = periods.map((period) => ({
	label: period.toUpperCase(),
	value: period
}));

export const globalsSchema = z.object({
	praetors: z.array(z.string()).optional(),
	period: z.enum(periods).optional(),
	start: z.string().optional(),
	end: z.string().optional(),
	filters: z
		.array(
			z.object({
				field: z.string(),
				op: z.enum(AppFilterOps),
				value: z.union([z.string(), z.boolean()])
			})
		)
		.optional()
});

const defaultGlobalSelection: GlobalSelection = {};

const GLOBAL_SELECTION_KEY = 'global-selection';

type GlobalSelectionStore = GlobalSelection & {
	setSelection: (selection: Partial<GlobalSelection>) => void;
	clearSelection: () => void;
};

export type FilterPeriod = (typeof periods)[number];

export type AnalyticsPeriod = FilterPeriod | '30d' | '90d';

const PERIOD_HOURS: Record<AnalyticsPeriod, number> = {
	'1h': 1,
	'24h': 24,
	'7d': 24 * 7,
	'30d': 24 * 30,
	'90d': 24 * 90,
	'all': 24 * 30
};

export function periodToRange(
	period: AnalyticsPeriod,
	start?: string,
	end?: string
): { start: string; end: string } {
	if (start && end) {
		return {
			start: new Date(start).toISOString(),
			end: new Date(end).toISOString()
		};
	}

	const now = new Date();
	const hours = PERIOD_HOURS[period];

	return {
		start: subHours(now, hours).toISOString(),
		end: now.toISOString()
	};
}

export function usePeriodRange(
	period: AnalyticsPeriod,
	start?: string,
	end?: string
): { start: string; end: string } {
	return useMemo(() => periodToRange(period, start, end), [period, start, end]);
}

export type GlobalSelection = z.infer<typeof globalsSchema>;

export type AppFilterItem = NonNullable<GlobalSelection['filters']>[number];

export function parseValidDateRange(
	start: unknown,
	end: unknown
): { start?: string; end?: string } {
	if (typeof start !== 'string' || typeof end !== 'string') {
		return {};
	}

	const startDate = new Date(start);
	const endDate = new Date(end);

	if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
		return {};
	}

	if (startDate.getTime() > endDate.getTime()) {
		return {};
	}

	return {
		start: startDate.toISOString(),
		end: endDate.toISOString()
	};
}

function validateTimeConstraint(state: GlobalSelection) {
	if (state.start && state.end) {
		return parseValidDateRange(state.start, state.end);
	}

	return { period: state.period };
}

export const useGlobalStore = create<GlobalSelectionStore>()(
	persist(
		(set) => ({
			...defaultGlobalSelection,

			setSelection: (selection) =>
				set((state) => {
					const result = globalsSchema.safeParse({ ...state, ...selection });
					return result.success ? result.data : state;
				}),
			clearSelection: () => set(defaultGlobalSelection)
		}),
		{
			name: GLOBAL_SELECTION_KEY,
			partialize: (state) => ({
				praetors: state.praetors,
				period: state.period,
				filters: state.filters
			})
		}
	)
);

export const getGlobals = (): GlobalSelection => {
	const state = useGlobalStore.getState();

	const globals: GlobalSelection = {
		praetors: state.praetors,
		filters: state.filters
	};

	const timeSelection = validateTimeConstraint(state);

	const praetors = queryClient.getQueryData(getListPraetorsQueryOptions().queryKey);

	// Filter out invalid praetor IDs, but keep empty array as "all"
	if (praetors && Array.isArray(globals.praetors) && globals.praetors.length > 0) {
		globals.praetors = globals.praetors.filter((id) => praetors.some((p: Praetor) => p.id === id));
	}

	if (globals.filters) {
		delete globals.filters;
	}

	return { ...globals, ...timeSelection };
};

export const clearGlobals = (): void => {
	useGlobalStore.getState().clearSelection();
};

export const setGlobals = (patch: Partial<GlobalSelection>) => {
	const globals = getGlobals();
	const { success, data, error } = globalsSchema.safeParse({ ...globals, ...patch });

	if (success) {
		if (!isEqual(data, globals)) {
			const { setSelection } = useGlobalStore.getState();
			setSelection(data);
		}

		return { success: true, data: data };
	}

	return { success: false, error: error };
};
